import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AccountReportProps, FiscalYearProps, ProfileData, StructureProps } from '../helpers/models/Models';

// Define types for your state and payloads
interface OrganizationStructure {
    org: StructureProps | null;
}
interface PageTitleProps {
    title: string;
}
interface SidebarDrawerProps {
    [key: string]: boolean; // Allow indexing by string for multiple sidebars
}
interface ProfileDataProps {
    data: ProfileData | null;
}
interface AccountReportDate {
    acc_rpt: AccountReportProps | null;
}
// Define the initial state type
interface orgState {
    SOFTWARE_ORG: OrganizationStructure;
    ACCOUNT_RPT_DATE: AccountReportDate;
    SET_FULL_SOFT_DATA: any,
    PAGE_TITLE: PageTitleProps;
    current_fiscal_year_list: {
        fiscal_year: FiscalYearProps;
    };
    ProfileDataDetails: ProfileDataProps;
    SidebarDrawer: SidebarDrawerProps; // Updated to handle multiple sidebars
}

// Define initial state
const initialState: orgState = {
    SOFTWARE_ORG: { org: null },
    ACCOUNT_RPT_DATE: { acc_rpt: null },
    PAGE_TITLE: { title: '' },
    SET_FULL_SOFT_DATA: {},
    current_fiscal_year_list: {
        fiscal_year: {
            fiscal_year_id: 0,
            fiscal_year_name: '',
            from_date_n: '',
            to_date_n: '',
            is_current_fiscal: 0,
            status: 0,
            updated_at: '',
            created_at: '',
        },
    },
    ProfileDataDetails: {
        data: null
    },
    SidebarDrawer: {
        sidebar1: false,
        trial_rpt: false,
        pl_report: false,
        voucher_rpt: false,
        balancesheet_report: false,
        mahasul_rtp: false,
        advance_voucher_rpt: false,
        day_book_rpt: false,
    }
};

// Create the slice
const orgSlice = createSlice({
    name: 'software',
    initialState,
    reducers: {
        SOFTWARE_NAME_ORG: (state, action: PayloadAction<StructureProps>) => {
            state.SOFTWARE_ORG.org = action.payload;
        },
        PAGE_TITLE_REDUCERS: (state, action: PayloadAction<PageTitleProps>) => {
            state.PAGE_TITLE = action.payload;
        },
        CURRENT_FISCAL_YEAR_REDUCER: (state, action: PayloadAction<FiscalYearProps>) => {
            state.current_fiscal_year_list.fiscal_year = action.payload;
        },
        CONSUMER_PROFILE_DETAILS_VIEW: (state, action: PayloadAction<ProfileData>) => {
            state.ProfileDataDetails.data = action.payload;
        },
        TOGGLE_SIDEBAR_DRAWER: (state, action: PayloadAction<string>) => {
            const sidebarId = action.payload;
            if (state.SidebarDrawer[sidebarId] !== undefined) {
                state.SidebarDrawer[sidebarId] = !state.SidebarDrawer[sidebarId];
            } else {
                // If sidebar doesn't exist, initialize it
                state.SidebarDrawer[sidebarId] = true;
            }
        },
        CLOSE_SIDEBAR_DRAWER: (state, action: PayloadAction<string>) => {
            const sidebarId = action.payload;
            // Close the specific sidebar state
            if (state.SidebarDrawer[sidebarId] !== undefined) {
                state.SidebarDrawer[sidebarId] = false;
            }
        },
        CLOSE_ALL_SIDEBARS: (state) => {
            Object.keys(state.SidebarDrawer).forEach(key => {
                state.SidebarDrawer[key] = false; // Ensure all sidebars are closed
            });
        },
        ACCOUNT_RPT_DATE: (state, action: PayloadAction<AccountReportProps>) => {
            state.ACCOUNT_RPT_DATE.acc_rpt = action.payload;
        },

        setFullDetailsData: (state, action: PayloadAction<any>) => {
            state.SET_FULL_SOFT_DATA = action.payload;
        },

    }
});

// Action creators
export const {
    SOFTWARE_NAME_ORG,
    PAGE_TITLE_REDUCERS,
    CURRENT_FISCAL_YEAR_REDUCER,
    CONSUMER_PROFILE_DETAILS_VIEW,
    TOGGLE_SIDEBAR_DRAWER,
    CLOSE_ALL_SIDEBARS,
    ACCOUNT_RPT_DATE,
    setFullDetailsData,
    CLOSE_SIDEBAR_DRAWER // New action for closing a specific sidebar
} = orgSlice.actions;

export default orgSlice.reducer;
